import { useState, useEffect, useCallback } from "react";
import { listAuditEntries, streamActivity, type AuditEntry } from "../api/client.js";

const LEVEL_STYLES: Record<string, { badge: string; dot: string }> = {
  info: { badge: "badge-blue", dot: "bg-blue-400" },
  warn: { badge: "badge-amber", dot: "bg-amber-400" },
  error: { badge: "badge-red", dot: "bg-red-400" },
  critical: { badge: "badge-red", dot: "bg-red-600" },
};

export function AuditPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [streaming, setStreaming] = useState(false);

  // Load initial snapshot
  useEffect(() => {
    listAuditEntries(500)
      .then((res) => setEntries(res.entries))
      .catch(() => {});
  }, []);

  // Connect to SSE stream for real-time updates
  useEffect(() => {
    const cleanup = streamActivity(
      (entry) => {
        setStreaming(true);
        setEntries((prev) => {
          if (prev.some((e) => e.id === entry.id)) return prev;
          const next = [entry, ...prev];
          return next.length > 500 ? next.slice(0, 500) : next;
        });
      },
      () => {
        setStreaming(false);
        // Fallback to polling on disconnect
        const interval = setInterval(() => {
          listAuditEntries(500)
            .then((res) => setEntries(res.entries))
            .catch(() => {});
        }, 5000);
        // Cleanup polling if component unmounts
        setTimeout(() => clearInterval(interval), 30_000);
      },
    );

    return cleanup;
  }, []);

  const filtered = entries.filter((entry) => {
    if (filter !== "all" && entry.level !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        entry.action.toLowerCase().includes(q) ||
        entry.category.toLowerCase().includes(q) ||
        entry.details?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-ink-100 bg-white px-6">
        <h1 className="text-sm font-semibold text-ink-900">Audit Trail</h1>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-8 text-xs"
              style={{ width: "220px" }}
              placeholder="Search events..."
            />
          </div>

          {/* Level filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs text-ink-700"
          >
            <option value="all">All levels</option>
            <option value="info">Info</option>
            <option value="warn">Warning</option>
            <option value="error">Error</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-6">
          {filtered.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <p className="text-sm text-ink-400">No matching events</p>
            </div>
          ) : (
            <div className="space-y-0">
              {filtered.map((entry, i) => (
                <AuditRow key={entry.id} entry={entry} isLast={i === filtered.length - 1} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer — live indicator */}
      <div className="flex h-10 shrink-0 items-center justify-between border-t border-ink-100 bg-white px-6">
        <div className="flex items-center gap-2 text-2xs text-ink-400">
          <span className={`h-1.5 w-1.5 rounded-full ${streaming ? "animate-pulse bg-emerald-500" : "bg-ink-300"}`} />
          {streaming ? "Live — streaming" : "Connecting..."}
        </div>
        <span className="text-2xs text-ink-400">{filtered.length} event{filtered.length !== 1 ? "s" : ""}</span>
      </div>
    </div>
  );
}

function AuditRow({ entry, isLast }: { entry: AuditEntry; isLast: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const style = LEVEL_STYLES[entry.level] ?? LEVEL_STYLES.info;

  return (
    <div className={`${isLast ? "" : "border-b border-ink-50"}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-surface-1"
      >
        {/* Timeline dot */}
        <div className="flex flex-col items-center">
          <span className={`h-2 w-2 rounded-full ${style.dot}`} />
        </div>

        {/* Timestamp */}
        <span className="w-24 shrink-0 font-mono text-2xs tabular-nums text-ink-400">
          {formatTime(entry.timestamp)}
        </span>

        {/* Level + Category */}
        <span className={`w-16 shrink-0 ${style.badge}`}>{entry.level}</span>
        <span className="w-28 shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-center font-mono text-2xs text-ink-600">
          {entry.category}
        </span>

        {/* Action */}
        <span className="flex-1 truncate text-sm text-ink-700">{entry.action}</span>

        {/* Expand */}
        {entry.details && (
          <svg
            className={`h-3.5 w-3.5 shrink-0 text-ink-400 transition-transform ${expanded ? "rotate-180" : ""}`}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 6l4 4 4-4" />
          </svg>
        )}
      </button>

      {expanded && entry.details && (
        <div className="ml-14 border-l-2 border-ink-100 pb-3 pl-4">
          <p className="text-xs text-ink-600">{entry.details}</p>
          <div className="mt-2 flex gap-3 text-2xs text-ink-400">
            {entry.agentId && <span>Agent: <span className="font-mono">{entry.agentId}</span></span>}
            {entry.userId && <span>User: <span className="font-mono">{entry.userId}</span></span>}
          </div>
        </div>
      )}
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  );
}
